import React, { useState, useEffect } from 'react';
import { ArrowLeft, Send, CheckCircle, XCircle, GitBranch, Eye, ShoppingCart, Building2, FileSignature, Truck, Download, FileText, Handshake, Pencil } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../components/ui/use-toast';
import { useParams, useNavigate } from 'react-router-dom';
import CreateRFQModal from './CreateRFQModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', step: 0 },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800', step: 1 },
  received: { label: 'Received', color: 'bg-yellow-100 text-yellow-800', step: 2 },
  compared: { label: 'Compared', color: 'bg-indigo-100 text-indigo-800', step: 3 },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', step: 4 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', step: -1 },
  converted: { label: 'Converted to PO', color: 'bg-emerald-100 text-emerald-800', step: 5 },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600', step: -1 }
};

const WORKFLOW_STEPS = ['Draft', 'Sent', 'Received', 'Compared', 'Approved', 'PO Created'];

const RFQDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [receiveModal, setReceiveModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [responseItems, setResponseItems] = useState([]);
  const [responseNotes, setResponseNotes] = useState('');
  const [actionNotes, setActionNotes] = useState('');

  useEffect(() => { fetchRFQ(); }, [id]);

  const fetchRFQ = async () => {
    try {
      const res = await fetch(`${API_URL}/api/procurement/rfqs/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setRfq(data);
      if (data.items) setResponseItems(data.items.map(i => ({ ...i, unit_price: i.unit_price || 0 })));
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load RFQ' });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, body = {}) => {
    setActionLoading(action);
    try {
      const res = await fetch(`${API_URL}/api/procurement/rfqs/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      const data = await res.json();
      toast({ title: 'Success', description: data.message });
      if (action === 'convert-to-po' && data.po_id) {
        navigate(`/admin/procurement/purchase-orders/${data.po_id}`);
        return;
      }
      fetchRFQ();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setActionLoading('');
    }
  };

  const handleReceiveSubmit = () => {
    handleAction('receive', {
      items: responseItems.map(i => ({
        product_name: i.product_name, description: i.description, quantity: i.quantity,
        unit: i.unit, unit_price: i.unit_price || 0, tax_rate: i.tax_rate || 0,
        total: (i.quantity || 1) * (i.unit_price || 0) * (1 + (i.tax_rate || 0) / 100)
      })),
      notes: responseNotes
    });
    setReceiveModal(false);
  };

  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'AED' }).format(amount);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A';

  if (loading) return <div className="flex items-center justify-center h-64 text-[#6C757D]">Loading RFQ...</div>;
  if (!rfq) return <div className="flex items-center justify-center h-64 text-[#6C757D]">RFQ not found</div>;

  const sc = STATUS_CONFIG[rfq.status] || STATUS_CONFIG.draft;
  const company = rfq.company_snapshot || {};
  const supplier = rfq.supplier_snapshot || {};
  const currentStep = sc.step;

  return (
    <div className="p-6" data-testid="rfq-detail-page">
      <button onClick={() => navigate('/admin/procurement/rfqs')} className="flex items-center text-[#6C757D] hover:text-[#2B2D42] mb-4" data-testid="back-to-rfqs">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to RFQs
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {(rfq.document_type || 'rfq') === 'loi' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase bg-[#023E8A]/10 text-[#023E8A] border border-[#023E8A]/20">
                <Handshake className="w-3 h-3" /> LOI
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/20">
                <FileText className="w-3 h-3" /> RFQ
              </span>
            )}
          </div>
          <h2 className="text-2xl font-bold text-[#2B2D42]" data-testid="rfq-number">{rfq.rfq_number}</h2>
          <div className="flex items-center space-x-3 mt-1">
            <Badge className={sc.color} data-testid="rfq-status">{sc.label}</Badge>
            <span className="text-sm text-[#6C757D]">Created {formatDate(rfq.created_at)}</span>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-wrap gap-y-2">
          <Button
            onClick={async () => {
              try {
                const res = await fetch(`${API_URL}/api/procurement/rfqs/${rfq.id}/pdf`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              } catch (err) {
                toast({ variant: 'destructive', title: 'Preview failed', description: String(err) });
              }
            }}
            variant="outline"
            className="border-[#023E8A] text-[#023E8A] hover:bg-[#023E8A]/5"
            data-testid="preview-pdf-btn"
          >
            <Eye className="h-4 w-4 mr-2" /> Preview PDF
          </Button>
          <Button
            onClick={async () => {
              try {
                const res = await fetch(`${API_URL}/api/procurement/rfqs/${rfq.id}/pdf?download=true`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${(rfq.document_type || 'rfq').toUpperCase()}_${rfq.rfq_number}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              } catch (err) {
                toast({ variant: 'destructive', title: 'Download failed', description: String(err) });
              }
            }}
            variant="outline"
            data-testid="download-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
          {rfq.status === 'draft' && (
            <Button
              variant="outline"
              onClick={() => setEditModal(true)}
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="edit-rfq-btn"
            >
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
          )}
          {rfq.status === 'draft' && (
            <Button onClick={() => handleAction('send')} disabled={!!actionLoading} className="bg-blue-600 hover:bg-blue-700" data-testid="send-rfq-btn">
              <Send className="h-4 w-4 mr-2" /> {actionLoading === 'send' ? 'Sending...' : `Send ${(rfq.document_type || 'rfq') === 'loi' ? 'LOI' : 'RFQ'}`}
            </Button>
          )}
          {rfq.status === 'sent' && (
            <Button onClick={() => setReceiveModal(true)} className="bg-yellow-600 hover:bg-yellow-700" data-testid="receive-rfq-btn">
              <Truck className="h-4 w-4 mr-2" /> Mark Received
            </Button>
          )}
          {rfq.status === 'received' && (
            <Button onClick={() => handleAction('compare', { notes: actionNotes || 'Comparison completed' })} disabled={!!actionLoading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="compare-rfq-btn">
              <GitBranch className="h-4 w-4 mr-2" /> {actionLoading === 'compare' ? 'Comparing...' : 'Compare'}
            </Button>
          )}
          {(rfq.status === 'received' || rfq.status === 'compared') && (
            <>
              <Button onClick={() => handleAction('approve', { notes: actionNotes || 'Approved' })} disabled={!!actionLoading} className="bg-green-600 hover:bg-green-700" data-testid="approve-rfq-btn">
                <CheckCircle className="h-4 w-4 mr-2" /> {actionLoading === 'approve' ? '...' : 'Approve'}
              </Button>
              <Button onClick={() => handleAction('reject', { reason: actionNotes || 'Rejected' })} disabled={!!actionLoading} variant="outline" className="border-red-500 text-red-500 hover:bg-red-50" data-testid="reject-rfq-btn">
                <XCircle className="h-4 w-4 mr-2" /> Reject
              </Button>
            </>
          )}
          {rfq.status === 'approved' && (
            <Button onClick={() => handleAction('convert-to-po')} disabled={!!actionLoading} className="bg-[#FF6B35] hover:bg-[#E5562D]" data-testid="convert-to-po-btn">
              <ShoppingCart className="h-4 w-4 mr-2" /> {actionLoading === 'convert-to-po' ? 'Converting...' : 'Convert to PO'}
            </Button>
          )}
        </div>
      </div>

      {/* Workflow Timeline */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            {WORKFLOW_STEPS.map((step, idx) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  idx <= currentStep ? 'bg-[#FF6B35] text-white' : 'bg-gray-200 text-gray-500'
                }`}>{idx + 1}</div>
                <span className={`ml-2 text-xs ${idx <= currentStep ? 'text-[#2B2D42] font-semibold' : 'text-[#6C757D]'}`}>{step}</span>
                {idx < WORKFLOW_STEPS.length - 1 && <div className={`w-12 h-0.5 mx-2 ${idx < currentStep ? 'bg-[#FF6B35]' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Notes */}
      {['received', 'compared'].includes(rfq.status) && (
        <Card className="mb-6">
          <CardContent className="pt-4">
            <label className="text-sm font-medium text-[#2B2D42]">Action Notes</label>
            <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm mt-1" rows="2" placeholder="Add notes for approval/rejection..." />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Parties */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-6">
                <div data-testid="rfq-issuer-info">
                  <h3 className="text-sm font-semibold text-[#2B2D42] mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#FF6B35]" /> From (Issuer)
                  </h3>
                  <div className="text-sm text-[#6C757D] space-y-1">
                    <div className="font-medium text-[#2B2D42] text-base">{company.legal_name || company.trading_name || 'N/A'}</div>
                    {company.address && <div>{company.address}</div>}
                    <div className="font-medium text-[#2B2D42]">{company.country || 'UAE'}</div>
                    {company.license_number && <div><span className="font-semibold">License:</span> {company.license_number}</div>}
                    {company.trn && <div><span className="font-semibold">TRN:</span> {company.trn}</div>}
                  </div>
                </div>
                <div data-testid="rfq-supplier-info">
                  <h3 className="text-sm font-semibold text-[#2B2D42] mb-3">To (Supplier)</h3>
                  <div className="text-sm text-[#6C757D] space-y-1">
                    <div className="font-medium text-[#2B2D42] text-base">{supplier.company_name || supplier.name || 'N/A'}</div>
                    <div>{supplier.email || 'N/A'}</div>
                    <div>{supplier.phone || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Requested Items */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">Requested Items</h3>
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm font-semibold text-[#2B2D42]">Product</th>
                    <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Qty</th>
                    <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Unit Price</th>
                    <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Tax</th>
                    <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(rfq.items || []).map((item, i) => (
                    <tr key={i} className="border-b border-gray-100" data-testid={`rfq-item-${i}`}>
                      <td className="py-3 text-sm"><div className="font-medium text-[#2B2D42]">{item.product_name}</div>{item.description && <div className="text-xs text-[#6C757D]">{item.description}</div>}</td>
                      <td className="py-3 text-sm text-right text-[#6C757D]">{item.quantity} {item.unit}</td>
                      <td className="py-3 text-sm text-right text-[#6C757D]">{item.unit_price != null ? formatCurrency(item.unit_price, rfq.currency) : 'TBD'}</td>
                      <td className="py-3 text-sm text-right text-[#6C757D]">{item.tax_rate || 0}%</td>
                      <td className="py-3 text-sm text-right font-medium text-[#2B2D42]">{item.total != null ? formatCurrency(item.total, rfq.currency) : 'TBD'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Supplier Response */}
          {rfq.response_items && (
            <Card className="border-green-200">
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold text-green-700 mb-4">Supplier Response</h3>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 text-sm font-semibold">Product</th>
                      <th className="text-right py-2 text-sm font-semibold">Qty</th>
                      <th className="text-right py-2 text-sm font-semibold">Unit Price</th>
                      <th className="text-right py-2 text-sm font-semibold">Tax</th>
                      <th className="text-right py-2 text-sm font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rfq.response_items.map((item, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-3 text-sm">{item.product_name}</td>
                        <td className="py-3 text-sm text-right">{item.quantity} {item.unit}</td>
                        <td className="py-3 text-sm text-right">{formatCurrency(item.unit_price, rfq.currency)}</td>
                        <td className="py-3 text-sm text-right">{item.tax_rate || 0}%</td>
                        <td className="py-3 text-sm text-right font-medium">{formatCurrency(item.total, rfq.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rfq.response_notes && <p className="mt-3 text-sm text-[#6C757D]">Notes: {rfq.response_notes}</p>}
                <p className="mt-2 text-xs text-[#6C757D]">Received: {formatDate(rfq.received_at)}</p>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {(rfq.notes || rfq.terms || rfq.comparison_notes || rfq.approval_notes || rfq.rejection_reason) && (
            <Card>
              <CardContent className="pt-6 space-y-3">
                {rfq.terms && <div><h4 className="text-sm font-semibold text-[#2B2D42]">Terms</h4><p className="text-sm text-[#6C757D]">{rfq.terms}</p></div>}
                {rfq.notes && <div><h4 className="text-sm font-semibold text-[#2B2D42]">Notes</h4><p className="text-sm text-[#6C757D]">{rfq.notes}</p></div>}
                {rfq.comparison_notes && <div><h4 className="text-sm font-semibold text-indigo-700">Comparison Notes</h4><p className="text-sm text-[#6C757D]">{rfq.comparison_notes}</p></div>}
                {rfq.approval_notes && <div><h4 className="text-sm font-semibold text-green-700">Approval Notes</h4><p className="text-sm text-[#6C757D]">{rfq.approval_notes}</p></div>}
                {rfq.rejection_reason && <div><h4 className="text-sm font-semibold text-red-700">Rejection Reason</h4><p className="text-sm text-[#6C757D]">{rfq.rejection_reason}</p></div>}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="border-[#FF6B35] border-2">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-[#2B2D42] mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-[#FF6B35]" /> Issuer Company</h3>
              <div className="text-sm space-y-1">
                <p className="font-bold text-[#FF6B35]">{company.trading_name || company.legal_name}</p>
                <p className="text-[#6C757D]">{company.country}</p>
                {company.license_number && <p className="text-[#6C757D]">License: {company.license_number}</p>}
                {company.trn && <p className="text-[#6C757D]">TRN: {company.trn}</p>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold text-[#2B2D42] mb-3">Details</h3>
              <div className="text-sm space-y-2">
                <div className="flex justify-between"><span className="text-[#6C757D]">Issue Date:</span><span>{formatDate(rfq.issue_date)}</span></div>
                <div className="flex justify-between"><span className="text-[#6C757D]">Due Date:</span><span>{formatDate(rfq.due_date)}</span></div>
                <div className="flex justify-between"><span className="text-[#6C757D]">Currency:</span><span className="font-bold text-[#FF6B35]">{rfq.currency}</span></div>
                <div className="flex justify-between"><span className="text-[#6C757D]">Items:</span><span>{(rfq.items || []).length}</span></div>
              </div>
            </CardContent>
          </Card>

          {rfq.converted_po_number && (
            <Card className="border-emerald-300">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-emerald-700 mb-2">Converted to PO</h3>
                <p className="text-lg font-bold text-emerald-700">{rfq.converted_po_number}</p>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate(`/admin/procurement/purchase-orders/${rfq.converted_po_id}`)}>
                  View Purchase Order
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Audit Trail */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold text-[#2B2D42] mb-3">Audit Trail</h3>
              <div className="text-xs space-y-2 text-[#6C757D]">
                <div>Created: {formatDate(rfq.created_at)}</div>
                {rfq.sent_at && <div>Sent: {formatDate(rfq.sent_at)}</div>}
                {rfq.received_at && <div>Received: {formatDate(rfq.received_at)}</div>}
                {rfq.approved_at && <div>Approved: {formatDate(rfq.approved_at)}</div>}
                {rfq.rejected_at && <div>Rejected: {formatDate(rfq.rejected_at)}</div>}
                {rfq.converted_at && <div>Converted: {formatDate(rfq.converted_at)}</div>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Receive Modal */}
      {receiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between">
              <h3 className="text-lg font-bold text-[#2B2D42]">Receive Supplier Response</h3>
              <button onClick={() => setReceiveModal(false)} className="text-gray-500">X</button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-[#6C757D]">Enter the supplier's quoted prices for each item.</p>
              {responseItems.map((item, i) => (
                <div key={i} className="border rounded-lg p-3 space-y-2">
                  <div className="font-medium text-sm">{item.product_name}</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-[#6C757D]">Qty</label>
                      <input type="number" value={item.quantity} className="w-full px-2 py-1 border rounded text-sm"
                        onChange={(e) => { const arr = [...responseItems]; arr[i] = {...arr[i], quantity: parseFloat(e.target.value) || 0}; setResponseItems(arr); }} />
                    </div>
                    <div>
                      <label className="text-xs text-[#6C757D]">Unit Price *</label>
                      <input type="number" step="0.01" value={item.unit_price} className="w-full px-2 py-1 border rounded text-sm"
                        onChange={(e) => { const arr = [...responseItems]; arr[i] = {...arr[i], unit_price: parseFloat(e.target.value) || 0}; setResponseItems(arr); }} />
                    </div>
                    <div>
                      <label className="text-xs text-[#6C757D]">Tax %</label>
                      <input type="number" value={item.tax_rate} className="w-full px-2 py-1 border rounded text-sm"
                        onChange={(e) => { const arr = [...responseItems]; arr[i] = {...arr[i], tax_rate: parseFloat(e.target.value) || 0}; setResponseItems(arr); }} />
                    </div>
                  </div>
                </div>
              ))}
              <div>
                <label className="text-sm font-medium">Response Notes</label>
                <textarea value={responseNotes} onChange={(e) => setResponseNotes(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm mt-1" rows="2" />
              </div>
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setReceiveModal(false)}>Cancel</Button>
                <Button onClick={handleReceiveSubmit} className="bg-yellow-600 hover:bg-yellow-700" data-testid="submit-receive-btn">
                  Confirm Receipt
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit RFQ/LOI Modal */}
      {editModal && rfq && (
        <CreateRFQModal
          isOpen={editModal}
          editingRfq={rfq}
          onClose={() => setEditModal(false)}
          onSuccess={() => {
            setEditModal(false);
            fetchRFQ();
          }}
        />
      )}
    </div>
  );
};

export default RFQDetail;
